<template>
  <div class="bg-white rounded-2xl w-full max-w-3xl">
    <div class="flex items-center justify-between p-4 border-b border-gray-200">
      <div class="text-lg font-semibold">Назначить курс</div>
      <button @click="$emit('close')" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <div class="p-4 space-y-4">
      <div v-if="!isFixedMode" class="flex gap-2">
        <button @click="activeTab='users'" :class="['px-3 py-1.5 rounded-lg text-sm', activeTab==='users' ? 'bg-primary text-white' : 'bg-gray-100']">Сотрудники</button>
        <button @click="activeTab='departments'" :class="['px-3 py-1.5 rounded-lg text-sm', activeTab==='departments' ? 'bg-primary text-white' : 'bg-gray-100']">Отделы</button>
        <button @click="activeTab='selected'" :class="['px-3 py-1.5 rounded-lg text-sm relative', activeTab==='selected' ? 'bg-primary text-white' : 'bg-gray-100']">
          Выбранные
          <span v-if="totalSelected > 0" class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{{ totalSelected }}</span>
        </button>
      </div>

      <input v-if="!isFixedMode" v-model="query" type="text" placeholder="Поиск" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#574AEA] focus:border-transparent" />

      <div v-if="!isFixedMode" class="flex items-center justify-between text-xs text-gray-700">
        <label v-if="activeTab==='users'" class="inline-flex items-center gap-2">
          <input
            type="checkbox"
            class="h-4 w-4"
            :checked="isAllUsersSelected"
            @change="toggleSelectAllUsers"
          />
          Выбрать всех
        </label>
        <label v-else-if="activeTab==='departments'" class="inline-flex items-center gap-2">
          <input
            type="checkbox"
            class="h-4 w-4"
            :checked="isAllDepartmentsSelected"
            @change="toggleSelectAllDepartments"
          />
          Выбрать все отделы
        </label>
      </div>

      <div v-if="activeTab==='users'">
        <div v-if="isUsersLoading" class="p-3 text-sm text-gray-500 flex items-center gap-2">
          <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="4" opacity="0.2"/><path d="M4 12a8 8 0 018-8" fill="none" stroke="currentColor" stroke-width="4"/></svg>
          Загрузка сотрудников...
        </div>
        <div v-else class="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100">
          <label v-for="u in filteredUsers" :key="u.ID" class="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50">
            <input type="checkbox" class="h-4 w-4" v-model="selectedUserIds" :value="String(u.ID)" :disabled="isFixedMode" />
            <div class="flex-1 min-w-0">
              <UserCard
                :avatarUrl="u.PERSONAL_PHOTO"
                :title="(u.LAST_NAME + ' ' + u.NAME).trim() || u.ID"
                :subtitle="u.WORK_POSITION"
                size="md"
                shape="rounded-full"
              />
            </div>
          </label>
          <div v-if="filteredUsers.length===0" class="p-3 text-sm text-gray-500">Ничего не найдено</div>
        </div>
      </div>

      <div v-else-if="activeTab==='departments' && !isFixedMode">
        <div v-if="isDepartmentsLoading" class="p-3 text-sm text-gray-500 flex items-center gap-2">
          <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="4" opacity="0.2"/><path d="M4 12a8 8 0 018-8" fill="none" stroke="currentColor" stroke-width="4"/></svg>
          Загрузка отделов...
        </div>
        <div v-else class="max-h-64 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-100">
          <div v-for="d in filteredDepartments" :key="d.ID">
            <div class="flex items-center gap-3 p-3">
              <input
                type="checkbox"
                class="h-4 w-4"
                :value="String(d.ID)"
                v-model="selectedDeptIds"
                @change="onToggleDept(d)"
              />
              <button
                type="button"
                @click.stop="toggleExpand(d)"
                class="w-5 h-5 text-gray-500 hover:text-gray-700"
              >
                <svg
                  class="w-5 h-5 transform transition-transform"
                  :class="{'rotate-90': expandedDepts[String(d.ID)]}"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-gray-900 truncate">{{ d.NAME }}</div>
              </div>
              <div class="text-xs text-gray-500 flex items-center gap-2 min-w-[64px] justify-end">
                <template v-if="isDeptLoading(String(d.ID))">
                  <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="4" opacity="0.2"/><path d="M4 12a8 8 0 018-8" fill="none" stroke="currentColor" stroke-width="4"/></svg>
                </template>
                <template v-else>
                  {{ deptUsersCount(d.ID) }} чел.
                </template>
              </div>
            </div>
            <div v-if="expandedDepts[String(d.ID)]" class="bg-gray-50 px-10 py-2 space-y-2">
              <div v-if="isDeptLoading(String(d.ID))" class="text-xs text-gray-500">Загрузка...</div>
              <div v-else>
                <div class="flex justify-end mb-2">
                  <button
                    type="button"
                    class="text-xs text-primary hover:underline"
                    @click="selectAllInDept(String(d.ID))"
                  >
                    Выбрать всех в отделе
                  </button>
                </div>
                <div v-for="grp in getPositionGroupsArr(String(d.ID))" :key="grp.name" class="mb-3">
                  <div class="text-xs font-medium text-gray-600 mb-1">{{ grp.name }}</div>
                  <div>
                    <label v-for="u in grp.users" :key="u.ID" class="flex items-center gap-2 py-1 pl-2">
                      <input type="checkbox" class="h-4 w-4" v-model="selectedUserIds" :value="String(u.ID)" />
                      <div class="text-sm text-gray-700 truncate">
                        {{ (u.LAST_NAME + ' ' + u.NAME).trim() || u.ID }}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="filteredDepartments.length===0" class="p-3 text-sm text-gray-500">Ничего не найдено</div>
        </div>
      </div>

      <!-- Выбранные пользователи -->
      <div v-else-if="activeTab==='selected'">
        <div v-if="totalSelected === 0" class="p-3 text-sm text-gray-500 text-center py-8">
          <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2a3 3 0 015.356-1.857"/>
          </svg>
          <p>Ничего не выбрано</p>
        </div>
        <div v-else class="max-h-96 overflow-y-auto space-y-2">
          <div v-for="user in selectedUsers" :key="user.ID" class="p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <div class="flex items-center gap-3">
              <UserCard
                :avatarUrl="user.PERSONAL_PHOTO"
                :title="(user.LAST_NAME + ' ' + user.NAME).trim()"
                :subtitle="user.WORK_POSITION"
                size="md"
                shape="rounded-full"
              />
              <button
                @click="removeSelectedUser(user.ID)"
                class="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                title="Убрать из выбора"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="block text-sm text-gray-600 mb-1">Дедлайн</label>
          <input v-model="deadline" type="datetime-local" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#574AEA] focus:border-transparent" />
        </div>
        <div class="flex items-end">
          <label class="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" class="h-4 w-4" v-model="createB24Tasks" /> Создавать задачи Bitrix24
          </label>
        </div>
      </div>

      <div class="flex items-center justify-between text-sm text-gray-600">
        <div>Выбрано: {{ totalSelected }} чел.</div>
        <div class="flex gap-2 flex-wrap justify-end">
          <button @click="$emit('close')" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Отменить</button>
          <button :disabled="isSubmitting || totalSelected===0" @click="submit()" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <svg v-if="isSubmitting" class="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="4" opacity="0.2"/><path d="M4 12a8 8 0 018-8" fill="none" stroke="currentColor" stroke-width="4"/></svg>
            {{ props.defaultSubmitMode === 'repeat' ? (isSubmitting ? 'Переназначение...' : 'Переназначить') : (isSubmitting ? 'Назначение...' : 'Назначить') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import { usePortalStore } from '@/stores/portalStore'
import { useToast } from 'vue-toastification'
import UserCard from '@/components/ui/UserCard.vue'

const props = defineProps({ 
  courseId: { type: String, required: true },
  preselectedUserIds: { type: Array, default: () => [] },
  defaultSubmitMode: { type: String, default: 'assign' }, // 'assign' | 'repeat'
  fixedUserIds: { type: Array, default: () => [] },
})
const emit = defineEmits(['close','assigned'])
const toast = useToast()

const activeTab = ref('users')
const usersQuery = ref('')
const departmentsQuery = ref('')
const query = computed({
  get() {
    return activeTab.value === 'departments' ? departmentsQuery.value : usersQuery.value
  },
  set(v) {
    if (activeTab.value === 'departments') departmentsQuery.value = v
    else usersQuery.value = v
  },
})
const users = ref([])
const departments = ref([])
const portalStore = usePortalStore()
const selectedUserIds = ref([])
const selectedDeptIds = ref([])
const excludedUserIds = ref([])
const deptUsersMap = ref({})
const deptUsersFullMap = ref({})
const deadline = ref('')
const createB24Tasks = ref(true)
const isSubmitting = ref(false)
const isUsersLoading = ref(true)
const isDepartmentsLoading = ref(true)
const deptLoading = ref({})
const expandedDepts = ref({})

const isFixedMode = computed(() => Array.isArray(props.fixedUserIds) && props.fixedUserIds.length > 0)

const preselectedSet = computed(() => new Set((props.preselectedUserIds || []).map((x) => String(x))))
const isRepeatMode = computed(() => props.defaultSubmitMode === 'repeat')

const activeUserIdSet = computed(() => {
  const set = new Set()
  for (const u of (users.value || [])) {
    set.add(String(u.ID))
  }
  return set
})

const filteredUsers = computed(() => {
  const q = usersQuery.value.trim().toLowerCase()
  let src = users.value
  if (isFixedMode.value) {
    const set = new Set((props.fixedUserIds || []).map(String))
    src = src.filter(u => set.has(String(u.ID)))
  }
  if (!q) return src
  return src.filter(u => `${u.LAST_NAME} ${u.NAME}`.toLowerCase().includes(q) || String(u.ID).includes(q))
})

const filteredDepartments = computed(() => {
  const q = departmentsQuery.value.trim().toLowerCase()
  const src = departments.value
  if (!q) return src
  return src.filter(d => d.NAME.toLowerCase().includes(q))
})

const deptChildrenMap = computed(() => {
  const map = {}
  for (const d of (departments.value || [])) {
    const parent = String(d.PARENT_ID ?? d.PARENT ?? '')
    if (!map[parent]) map[parent] = []
    map[parent].push(String(d.ID))
  }
  return map
})

const collectDescendants = (rootId) => {
  const res = []
  const stack = [String(rootId)]
  const seen = new Set()
  while (stack.length) {
    const id = String(stack.pop())
    const children = deptChildrenMap.value[id] || []
    for (const c of children) {
      if (!seen.has(c)) {
        seen.add(c)
        res.push(c)
        stack.push(c)
      }
    }
  }
  return res
}

const deptUsersCount = (deptId) => {
  const id = String(deptId)
  const m = deptUsersMap.value[id]
  if (Array.isArray(m)) return m.length
  // запасной расчёт по данным портала (может отличаться по включению подотделов)
  try {
    const all = Array.isArray(portalStore?.portalData?.allUsers) ? portalStore.portalData.allUsers : []
    const desc = collectDescendants(id)
    const set = new Set([id, ...desc])
    return all.filter(u => {
      const arr = Array.isArray(u?.UF_DEPARTMENT) ? u.UF_DEPARTMENT.map(String) : []
      return arr.some(did => set.has(did))
    }).length
  } catch { return 0 }
}

const loadUsers = async () => {
  // только сотрудники компании (без сторонних контактов из коллаба)
  try { users.value = await api.getBitrixUsers(true) } catch { users.value = [] } finally { isUsersLoading.value = false }
}
const loadDepartments = async () => {
  try { departments.value = await api.getBitrixDepartments() } catch { departments.value = [] } finally { isDepartmentsLoading.value = false }
}

const onToggleDept = async (dept) => {
  const id = String(dept.ID)
  if (!deptUsersMap.value[id]) {
    deptLoading.value[id] = true
    try {
      // Используем кеш из portalStore
      const list = await portalStore.getDepartmentUsers(id)
      deptUsersMap.value[id] = list.map(u => String(u.ID))
      deptUsersFullMap.value[id] = list
      // Автоматическая отметка отдела, если все сотрудники уже выбраны
      const allIn = deptUsersMap.value[id].length > 0 && deptUsersMap.value[id].every(u => selectedUserIds.value.map(String).includes(String(u)))
      if (allIn && !selectedDeptIds.value.includes(id)) selectedDeptIds.value.push(id)
    } catch { deptUsersMap.value[id] = [] }
    finally { deptLoading.value[id] = false }
  }
}

const ensureDeptLoaded = async (deptId) => {
  const id = String(deptId)
  if (deptUsersMap.value[id] || deptLoading.value[id]) return
  deptLoading.value[id] = true
  try {
    // Используем кеш из portalStore
    const list = await portalStore.getDepartmentUsers(id)
    deptUsersMap.value[id] = list.map(u => String(u.ID))
    deptUsersFullMap.value[id] = list
    const allIn = deptUsersMap.value[id].length > 0 && deptUsersMap.value[id].every(u => selectedUserIds.value.map(String).includes(String(u)))
    if (allIn && !selectedDeptIds.value.includes(id)) selectedDeptIds.value.push(id)
  } catch { deptUsersMap.value[id] = [] }
  finally { deptLoading.value[id] = false }
}

const totalSelected = computed(() => {
  const s = new Set(selectedUserIds.value.map(String))
  for (const id of selectedDeptIds.value.map(String)) {
    const arr = deptUsersMap.value[id] || []
    for (const u of arr) s.add(String(u))
  }
  excludedUserIds.value.forEach((id) => s.delete(String(id)))
  if (isRepeatMode.value) return s.size
  let count = 0
  s.forEach((id) => { if (!preselectedSet.value.has(String(id))) count++ })
  return count
})

// Список выбранных пользователей
const selectedUsers = computed(() => {
  const s = new Set(selectedUserIds.value.map(String))
  for (const id of selectedDeptIds.value.map(String)) {
    const arr = deptUsersMap.value[id] || []
    for (const u of arr) s.add(String(u))
  }
  excludedUserIds.value.forEach((id) => s.delete(String(id)))

  const result = []
  const known = new Set()
  for (const u of (users.value || [])) {
    const id = String(u.ID)
    if (s.has(id)) {
      result.push(u)
      known.add(id)
    }
  }
  for (const id of Array.from(s)) {
    if (known.has(String(id))) continue
    const uid = String(id)
    result.push({
      ID: uid,
      LAST_NAME: '',
      NAME: `Неактивный сотрудник (ID ${uid})`,
      WORK_POSITION: '',
      PERSONAL_PHOTO: '',
      isInactiveUser: true,
    })
  }
  return result
})

// Удаление выбранного пользователя
const removeSelectedUser = (userId) => {
  const uid = String(userId)
  if (!excludedUserIds.value.includes(uid)) excludedUserIds.value.push(uid)
  selectedUserIds.value = selectedUserIds.value.filter(id => String(id) !== uid)
}

const isAllUsersSelected = computed(() => {
  const list = filteredUsers.value
  if (!list || list.length === 0) return false
  const set = new Set(selectedUserIds.value.map(String))
  return list.every(u => set.has(String(u.ID)))
})

const toggleSelectAllUsers = (e) => {
  const checked = e?.target?.checked
  const current = new Set(selectedUserIds.value.map(String))
  const list = filteredUsers.value || []
  if (checked) {
    list.forEach(u => {
      const id = String(u.ID)
      if (isRepeatMode.value || !preselectedSet.value.has(id)) current.add(id)
    })
  } else {
    const toRemove = new Set(list.map(u => String(u.ID)))
    selectedUserIds.value = selectedUserIds.value.filter(id => !toRemove.has(String(id)))
    return
  }
  selectedUserIds.value = Array.from(current)
}

const isAllDepartmentsSelected = computed(() => {
  const list = filteredDepartments.value
  if (!list || list.length === 0) return false
  const set = new Set(selectedDeptIds.value.map(String))
  return list.every(d => set.has(String(d.ID)))
})

const toggleSelectAllDepartments = async (e) => {
  const checked = e?.target?.checked
  const current = new Set(selectedDeptIds.value.map(String))
  const list = filteredDepartments.value || []
  if (checked) {
    list.forEach(d => current.add(String(d.ID)))
    try {
      await Promise.allSettled(list.map(d => ensureDeptLoaded(String(d.ID))))
    } catch (e) { console.error('Ошибка при предварительной загрузке отделов:', e) }
  } else {
    const toRemove = new Set(list.map(d => String(d.ID)))
    selectedDeptIds.value = selectedDeptIds.value.filter(id => !toRemove.has(String(id)))
    return
  }
  selectedDeptIds.value = Array.from(current)
}

const toggleExpand = async (dept) => {
  const id = String(dept.ID)
  expandedDepts.value[id] = !expandedDepts.value[id]
  if (expandedDepts.value[id]) await ensureDeptLoaded(id)
}

const selectAllInDept = async (deptId) => {
  const id = String(deptId)
  await ensureDeptLoaded(id)
  const arr = (deptUsersMap.value[id] || []).map(String)
  const current = new Set(selectedUserIds.value.map(String))
  arr.forEach(u => {
    const uid = String(u)
    if (isRepeatMode.value || !preselectedSet.value.has(uid)) current.add(uid)
  })
  selectedUserIds.value = Array.from(current)
}

const getPositionGroupsArr = (deptId) => {
  const id = String(deptId)
  const list = Array.isArray(deptUsersFullMap.value[id]) ? deptUsersFullMap.value[id] : []
  const groups = {}
  for (const u of list) {
    const key = ((u?.WORK_POSITION ?? '') || '').toString().trim() || 'Без должности'
    if (!groups[key]) groups[key] = []
    groups[key].push(u)
  }
  return Object.keys(groups)
    .sort((a, b) => a.localeCompare(b, 'ru'))
    .map(name => ({ name, users: groups[name] }))
}

const submit = async () => {
  const mode = (props.defaultSubmitMode === 'repeat') ? 'repeat' : 'assign'
  const s = new Set(selectedUserIds.value.map(String))
  for (const id of selectedDeptIds.value.map(String)) {
    const arr = deptUsersMap.value[id] || []
    for (const u of arr) s.add(String(u))
  }
  excludedUserIds.value.forEach((id) => s.delete(String(id)))
  const allIds = Array.from(s)
  const toSend = mode === 'repeat'
    ? allIds
    : allIds.filter(id => !preselectedSet.value.has(String(id)))

  const activeIds = toSend.filter((id) => activeUserIdSet.value.has(String(id)))
  const skippedInactiveCount = toSend.length - activeIds.length
  if (skippedInactiveCount > 0) {
    try {
      toast.warning(`Пропущено неактивных сотрудников: ${skippedInactiveCount}`)
    } catch (e) {
      console.warn('Не удалось показать предупреждение о неактивных сотрудниках', e)
    }
  }

  const userIds = activeIds
  if (userIds.length === 0) {
    try {
      toast.warning('Нельзя назначить курс неактивным сотрудникам')
    } catch (e) {
      console.warn('Не удалось показать предупреждение о назначении неактивным сотрудникам', e)
    }
    return
  }
  isSubmitting.value = true
  try {
    const payload = { userIds, createB24Tasks: createB24Tasks.value }
    if (deadline.value) payload.dueAt = new Date(deadline.value).toISOString()
    
    // Получаем токен текущего пользователя через BX24 для действий от его имени
    if (typeof window.BX24 !== 'undefined') {
      try {
        const authData = window.BX24.getAuth()
        const userAccessToken = authData?.access_token || authData?.AUTH_ID || authData?.auth_id
        if (userAccessToken) payload.userAccessToken = userAccessToken
        if (authData?.refresh_token) payload.userRefreshToken = authData.refresh_token
        if (authData?.expires_in) payload.userTokenExpiresIn = Number(authData.expires_in)
      } catch (e) {
        console.warn('Не удалось получить токен пользователя через BX24:', e)
      }
    }
    
    if (mode === 'repeat') {
      await api.repeatAssignments(props.courseId, payload)
    } else {
      await api.assignCourse(props.courseId, payload)
    }
    emit('assigned', { userIds })
    try {
      toast.success(`Курс ${mode === 'repeat' ? 'переназначен' : 'назначен'} ${userIds.length} сотрудникам`)
    } catch (e) { /* noop */ }
    emit('close')
  } catch (e) {
    // silent
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  await Promise.all([loadUsers(), loadDepartments()])
  if (isFixedMode.value) {
    selectedUserIds.value = props.fixedUserIds.map((x) => String(x))
  } else if (Array.isArray(props.preselectedUserIds) && props.preselectedUserIds.length > 0) {
    selectedUserIds.value = props.preselectedUserIds.map((x) => String(x))
  }
})

const isDeptLoading = (id) => Boolean(deptLoading.value[String(id)])

// При изменении выбранных пользователей — переоцениваем уже загруженные отделы
watch(selectedUserIds, () => {
  const selectedSet = new Set(selectedUserIds.value.map(String))
  excludedUserIds.value = excludedUserIds.value.filter((id) => !selectedSet.has(String(id)))
  for (const [id, usersOfDept] of Object.entries(deptUsersMap.value)) {
    if (!usersOfDept || (usersOfDept || []).length === 0) continue
    const allIn = (usersOfDept || []).every(u => selectedUserIds.value.map(String).includes(String(u)))
    const idx = selectedDeptIds.value.indexOf(String(id))
    if (allIn && idx === -1) selectedDeptIds.value.push(String(id))
  }
})

// Подгрузка численности для видимых отделов (только при фильтрации)
watch(filteredDepartments, (list) => {
  const subset = (list || []).slice(0, 100)
  subset.forEach(d => ensureDeptLoaded(d.ID))
})
</script>
